import React, { useState } from "react";
import {
  Flex,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Text,
  Tag,
  TagLabel,
  TableContainer,
  Badge,
  IconButton,
  Menu,
  MenuOptionGroup,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
} from "@chakra-ui/react";
import { useAppContext } from "../../contexts/AppContext";
import useTasks from "../../hooks/useTasks";
import { EllipsisVertical, ChevronDown, ChevronUp } from "lucide-react";
import TableFilters from "../general/TableFilters";

const TasksTable = ({
  tasks,
  handleCheckboxChange,
  justCompletedTasks = [],
  onRowClick,
  filters,
  setFilters,
  extraFilters = {},
  extraLabelMap = {},
}) => {
  const { formatDate, tableSize, toggleTableSize } = useAppContext();
  const { isOverdue } = useTasks();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const toggleSortDirection = () => {
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const labelMap = {
    category: "Category",
    ...extraLabelMap,
  };

  const filterOptions = {
    category: [
      "portfolio management",
      "operation",
      "risk",
      "compliance",
      "valuation",
    ],
    ...extraFilters,
  };

  const filteredTasks = tasks.filter((task) => {
    const query = searchQuery.toLowerCase();

    const matchesSearch =
      task.label.toLowerCase().includes(query) ||
      task.fund.toLowerCase().includes(query) ||
      (task.investmentStrategy &&
        task.investmentStrategy.toLowerCase().includes(query));

    const matchesCategory =
      !filters.category ||
      filters.category.length === 0 ||
      filters.category.includes(task.category);

    const taskStatus = task.completed
      ? "Completed"
      : task.dueDate && new Date(task.dueDate) < new Date()
      ? "Overdue"
      : "Pending";

    const matchesStatus =
      !filters.status ||
      filters.status.length === 0 ||
      filters.status.includes(taskStatus);

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const dateComparison = new Date(a.dueDate) - new Date(b.dueDate);
    return sortDirection === "asc" ? dateComparison : -dateComparison;
  });

  return (
    <Flex direction="column" gap={8}>
      {/* Always show search + filters */}
      <Flex gap={2} flex={1} justifyContent={"space-between"}>
        <HStack>
          <Input
            placeholder="Search by task name or fund"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            w={"260px"}
          />
          <TableFilters
            filters={filters}
            setFilters={setFilters}
            filterOptions={filterOptions}
            labelMap={labelMap}
          />
        </HStack>
        <Menu>
          <MenuButton
            as={IconButton}
            icon={<EllipsisVertical size={20} />}
            variant="outline"
          />
          <MenuList>
            <MenuOptionGroup defaultValue="asc" title="Display" type="radio">
              <MenuItem onClick={toggleTableSize}>
                {tableSize === "sm" ? "Expand view" : "Compact view"}
              </MenuItem>
            </MenuOptionGroup>
          </MenuList>
        </Menu>
      </Flex>

      <TableContainer>
       
          <Table variant="simple" size={tableSize} className="hasCheckbox">
            <Thead>
              <Tr>
                <Th></Th>
                <Th>Task Name</Th>
                <Th>Fund</Th>
                <Th>Category</Th>
                <Th onClick={toggleSortDirection} cursor="pointer">
                  <Flex gap={1} alignItems="center">
                    Due Date{" "}
                    {sortDirection === "asc" ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </Flex>
                </Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
            {sortedTasks.length > 0 ? (
              sortedTasks.map((task) => (
                <Tr
                  key={task.id}
                  opacity={justCompletedTasks.includes(task.id) ? 0.4 : 1}
                  style={{ transition: "opacity 1s ease", cursor: "pointer" }}>
                  <Td>
                    <Checkbox
                      isChecked={task.completed}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange(task.id);
                      }}
                    />
                  </Td>
                  <Td onClick={() => onRowClick(task)}>
                    <Text
                      as={
                        task.completed || justCompletedTasks.includes(task.id)
                          ? "del"
                          : "span"
                      }
                      color={
                        task.completed || justCompletedTasks.includes(task.id)
                          ? "gray.400"
                          : "black"
                      }
                      fontWeight={500}>
                      {task.label}
                    </Text>
                  </Td>
                  <Td whiteSpace={"nowrap"} onClick={() => onRowClick(task)}>
                    {task.fund && (
                      <Tag colorScheme="brand">
                        <TagLabel>{task.fund}</TagLabel>
                      </Tag>
                    )}
                  </Td>
                  <Td onClick={() => onRowClick(task)}>
                    <Tag>
                      <TagLabel textTransform={"capitalize"}>
                        {task.category}
                      </TagLabel>
                    </Tag>
                  </Td>
                  <Td onClick={() => onRowClick(task)}>
                    <Badge
                      colorScheme={
                        task.completed
                          ? ""
                          : isOverdue(task.dueDate)
                          ? "error"
                          : ""
                      }>
                      {formatDate(task.dueDate)}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={
                        task.completed
                          ? "green"
                          : isOverdue(task.dueDate)
                          ? "red"
                          : "yellow"
                      }>
                      {task.completed
                        ? "Completed"
                        : isOverdue(task.dueDate)
                        ? "Overdue"
                        : "Pending"}
                    </Badge>
                  </Td>
                </Tr>
              ))
            ) : (
          
               <Tr>
                              <Td colSpan="6">
                                <Text textAlign="center" py={10} color="gray.500">
                                No tasks match the filters
                                </Text>
                              </Td>
                            </Tr>
            )}
            
            </Tbody>
          </Table>
        
      </TableContainer>
    </Flex>
  );
};

export default TasksTable;
